import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Globe } from "lucide-react";

import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CampaignProvider } from "@/context/CampaignContext";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { WelcomeModal } from "@/components/WelcomeModal";
import { ReturningUserBanner } from "@/components/ReturningUserBanner";
import { CampaignBanner } from "@/components/CampaignBanner";
import { TierCelebration } from "@/components/TierCelebration";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Admin from "@/pages/Admin";
import MyOrders from "@/pages/MyOrders";
import Rewards from "@/pages/Rewards";
import Contact from "@/pages/Contact";
import Returns from "@/pages/Returns";
import Reviews from "@/pages/Reviews";

const Shell = ({ children }) => {
  const { dir, lang, toggle } = useLanguage();
  const location = useLocation();
  const isAuth = location.pathname === "/login" || location.pathname === "/register";
  return (
    <div dir={dir} className="min-h-screen flex flex-col">
      {!isAuth && <CampaignBanner />}
      {!isAuth && <Header />}
      <main className="flex-1">{children}</main>
      {!isAuth && <Footer />}
      <CartDrawer />
      <WelcomeModal />
      <ReturningUserBanner />
      <TierCelebration />
      {isAuth && (
        <button
          onClick={toggle}
          data-testid="auth-lang-toggle"
          className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-2 text-xs border border-[#27272A] rounded-full bg-[#0A0A0B]/80 backdrop-blur hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
        >
          <Globe size={14} />
          <span className="num-ltr">{lang === "ar" ? "EN" : "العربية"}</span>
        </button>
      )}
      <Toaster theme="dark" position={dir === "rtl" ? "top-left" : "top-right"} richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <CampaignProvider>
              <Shell>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/:category" element={<Shop />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/my-orders" element={<MyOrders />} />
                  <Route path="/rewards" element={<Rewards />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/returns" element={<Returns />} />
                  <Route path="/reviews" element={<Reviews />} />
                </Routes>
              </Shell>
            </CampaignProvider>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
